import { Merge } from "lucide-react";
import type { ContactMerge } from "../../api/types";
import { Badge } from "../../components";
import { t } from "../../i18n/t";

/**
 * T8 deferred item 2a (design §7.5): derived off the generated `ContactMerge` (`ContactMergeOut`) rather
 * than hand-declared, so `tsc` catches field-name drift the way `TaxRow` now does. `decidable` and
 * `message` are enriched web-only, from the row's own open (or newest) CONTACT review item
 * (`ReviewScreen.tsx`) - `decidable` is the fix for the reported defect: before, Accept/Reject were
 * enabled on every row and POSTed nothing, because CONTACT items are WARN-only and the old lookup
 * searched BLOCK items only.
 *
 * Redrawn as ReviewContacts.html: one card per proposal — the source and destination contacts as sunken
 * sides, the `merge` icon between them, the rule pill, and the Accept/Reject split control (`.seg`).
 */
export type MergeRow = ContactMerge & {
  decidable?: boolean;
  message?: string | null;
};

/** As in `CoaTable`, the map holds the translated string so the "(b)" i18n assertion can see the reference. */
const TIER_LABELS: Readonly<Record<number, string>> = {
  0: t("mapping.tier_0"),
  1: t("mapping.tier_1"),
  2: t("mapping.tier_2"),
};

/**
 * §B4.4. `ContactMergeRow` is `mapping_version_id, source_ext_id, target_ext_id, tier, accepted` and nothing else,
 * so the matched field is **not** rendered — the tier *is* the rule, and its meaning is rendered in words. Tier 2
 * is advisory: `mapping.tier_2` says "a warning, not a merge" in the cell itself.
 */
export function ContactMerges({
  rows,
  onDecide,
  disabled,
  targetKey = null,
}: {
  rows: readonly MergeRow[];
  onDecide: (sourceExtId: string, accepted: boolean) => void;
  disabled: boolean;
  /** xmg-webux: the row "Open the tab to choose" jumped to (highlighted, scrolled to and focused). */
  targetKey?: string | null;
}): JSX.Element {
  return (
    <div className="mergegrid" role="group" aria-label={t("review.caption_contacts")}>
      {rows.map((row) => {
        const label = row.tier === null || row.tier === undefined ? undefined : TIER_LABELS[row.tier];
        // T8 deferred item 2a: enabled ONLY while the row's own CONTACT item is still open. A tier-0 row
        // (no item at all), an already-decided row, or a row whose item was resolved elsewhere all give
        // `decidable !== true`, so both buttons are disabled and neither POSTs anything - closing the
        // reported defect (I5: no enabled control on this tab is ever a no-op).
        const enabled = !disabled && row.decidable === true;
        return (
          <article
            className={row.source_ext_id === targetKey ? "merge is-target" : "merge"}
            key={row.source_ext_id}
            data-rowkey={row.source_ext_id}
            tabIndex={-1}
          >
            <div className="merge__side">
              <span className="eyebrow">{t("mapping.merge_source")}</span>
              <span className="merge__name">{row.source_ext_id}</span>
            </div>
            <span className="merge__icon" aria-hidden="true">
              <Merge size={20} strokeWidth={2} />
            </span>
            <div className="merge__side">
              <span className="eyebrow">{t("mapping.merge_destination")}</span>
              <span className="merge__name">{row.target_ext_id ?? ""}</span>
            </div>
            <div className="merge__decision">
              <Badge tone={row.tier === 2 ? "warn" : "neutral"}>
                <span data-testid={`merge-tier-${row.source_ext_id}`}>{label ?? ""}</span>
              </Badge>
              {/* The message span always renders (empty when the row has none) — the row's tests address
                  it by testid either way. */}
              <span className="cellwrap muted" data-testid={`merge-message-${row.source_ext_id}`}>
                {row.message ?? ""}
              </span>
              <span data-testid={`merge-decision-${row.source_ext_id}`} className="failcard__meta">
                {row.accepted === true
                  ? t("mapping.accepted")
                  : row.accepted === false
                    ? t("mapping.rejected")
                    : t("mapping.undecided")}
              </span>
              <div className="seg" role="group" aria-label={t("mapping.decision_label")}>
                <button type="button" disabled={!enabled} onClick={() => onDecide(row.source_ext_id, true)}>
                  {t("mapping.accept")}
                </button>
                <button type="button" disabled={!enabled} onClick={() => onDecide(row.source_ext_id, false)}>
                  {t("mapping.reject")}
                </button>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
